import * as math from 'mathjs';

/**
 * Amount fields in the categorization UI are bound to free-text `v-model`s, so a line's `amount`
 * can be a `BigNumber` (when prepopulated) or a plain string the user is mid-typing ('1,', '1.2.3',
 * 'abc', '0x10', ...). These helpers are the single place that decides what counts as a valid
 * amount and how to coerce one safely, so every consumer (totals, collapse math, fiat display,
 * validation) behaves consistently and none of them throw on in-progress input.
 */

// Runtime check for a mathjs BigNumber (decimal.js instance); `math.isBigNumber` is not in the types.
const isBigNumber = (value: unknown): value is math.BigNumber =>
  (math as unknown as { isBigNumber(x: unknown): boolean }).isBigNumber(value);

// mathjs BigNumber is a decimal.js instance with isFinite()/isNaN() at runtime (not in its TS types).
const isFiniteBigNumber = (bn: math.BigNumber): boolean => {
  const d = bn as unknown as { isFinite(): boolean; isNaN(): boolean };
  return d.isFinite() && !d.isNaN();
};

// A decimal number with optional sign and optional exponent: '1.5', '-.5', '200', '1e-8', '1.2E+9'.
// Exponent notation is allowed because mathjs stringifies small/large BigNumbers to it (e.g.
// bignumber('0.00000001').toString() === '1e-8'), so a prepopulated amount edited via v-model can
// legitimately arrive in this form. Still rejects thousands separators ('1,000'), hex ('0x10'),
// 'Infinity'/'NaN', whitespace, and partial input ('1.', '1,', '1e', '-').
const DECIMAL_AMOUNT_RE = /^-?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/;

/**
 * Parse a free-text / BigNumber amount into a finite BigNumber, or return `null` if it is empty or
 * not a valid decimal number. Never throws.
 */
export const parseAmount = (value: unknown): math.BigNumber | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  // Fast path: an already-constructed BigNumber (the prepopulated case) needs no re-parse/alloc.
  if (isBigNumber(value)) {
    return isFiniteBigNumber(value) ? value : null;
  }
  // Reject non-canonical strings up front (this also rejects 'NaN'/'Infinity'/'1,000'/hex).
  if (typeof value === 'string' && !DECIMAL_AMOUNT_RE.test(value)) {
    return null;
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return null;
  }
  let bn: math.BigNumber;
  try {
    bn = math.bignumber(value as never);
  } catch {
    return null;
  }
  return isFiniteBigNumber(bn) ? bn : null;
};

/** True when `value` is a usable numeric amount (a finite canonical decimal or BigNumber). */
export const isValidAmount = (value: unknown): boolean => parseAmount(value) !== null;

/** Coerce any amount to a BigNumber for display/math, treating invalid/empty input as 0. Never throws. */
export const safeAmount = (value: unknown): math.BigNumber => parseAmount(value) ?? math.bignumber(0);
